import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Select from 'react-select';
import { TenantActions } from "redux/actions/settings/TenantActions";
import { StoreModel } from "models/StoreModel";
import { LoginUser } from "models/LoginUser";
import { Address, PaymentType, Tenant } from "models/Tenant";
import { Translations } from "models/Translations";
import { PageContentHeader } from "app/components/common/PageContentHeader";
import Translator from "app/components/common/Translator";
import { Translate } from "infrastructure/mapping/Translate";

interface Props {
    fetchCurrentTenant: typeof TenantActions.fetchCurrentTenant;
    editCurrentTenant: typeof TenantActions.editCurrentTenant;
    currentTenant: Tenant | null;
    loginUser: LoginUser;
    translations: Translations;
}

interface IState {
    name: string;
    address: Address;
    paymentType: PaymentType | null;
    invoiceDay: number;
    billingAddress: Address;
}

const textFields: (keyof Address)[] = ['name', 'firstName', 'street', 'zipCode', 'city', 'state', 'country'];
const emptyAddress = (): Address => ({ id: 0, name: '', firstName: '', isCompany: false, street: '', city: '', state: '', country: '', zipCode: '' });

class TenantSettings extends React.Component<Props, IState> {
    public readonly state: IState = this.fromTenant(this.props.currentTenant);

    componentDidMount() {
        if (this.loggedIn()) {
            this.props.fetchCurrentTenant(this.props.loginUser.token);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.currentTenant !== this.props.currentTenant) {
            // oxlint-disable-next-line react/no-did-update-set-state -- guarded by the prevProps comparison
            this.setState(this.fromTenant(this.props.currentTenant));
        }
    }

    fromTenant(t: Tenant | null): IState {
        const billing = t?.payment?.billingAddress;
        return {
            name: t?.name ?? '',
            address: { ...emptyAddress(), ...t?.address },
            paymentType: t?.payment?.paymentType ?? null,
            invoiceDay: t?.payment?.invoiceDay || 1,
            // a billing address shared with the tenant address is shown as empty, i.e. "use the tenant address"
            billingAddress: billing && billing.id !== t?.address?.id ? { ...emptyAddress(), ...billing } : emptyAddress(),
        };
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    setAddress(key: 'address' | 'billingAddress', field: keyof Address, value: string | boolean) {
        this.setState(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    }

    submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const t = this.props.currentTenant!;
        // the backend requires a name on an address, an address without one is not sent
        const orNull = (a: Address) => a.name.trim() ? a : null;
        this.props.editCurrentTenant(this.props.loginUser.token, {
            ...t,
            name: this.state.name,
            address: orNull(this.state.address),
            // shortcut: an emptied billing address is not sent, so a separate one already stored is kept; needs a backend flag to reset it
            payment: this.state.paymentType ? {
                paymentType: this.state.paymentType,
                invoiceDay: this.state.invoiceDay,
                billingAddress: orNull(this.state.billingAddress),
            } : null,
        });
    }

    addressFields(key: 'address' | 'billingAddress') {
        const a = this.state[key];
        return (
            <>
                {textFields.map(f => (
                    <div className="c-form__group" key={f}>
                        <label className={'c-form__label' + (f === 'name' && key === 'address' ? ' c-form__label--required' : '')} htmlFor={key + '.' + f}><Translator text={f} /></label>
                        <input className="c-form__control" id={key + '.' + f} type="text" value={a[f] as string ?? ''} onChange={e => this.setAddress(key, f, e.currentTarget.value)} />
                    </div>
                ))}
                <div className="c-form__group">
                    <label className="c-form__label" htmlFor={key + '.isCompany'}><Translator text="isCompany" /></label>
                    <input id={key + '.isCompany'} type="checkbox" checked={a.isCompany} onChange={e => this.setAddress(key, 'isCompany', e.currentTarget.checked)} />
                </div>
            </>
        );
    }

    render() {
        if (!this.loggedIn() || !this.props.currentTenant) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Tenant" />
                    <Translator text="Loading" />
                </div>
            )
        }
        const options = Object.values(PaymentType).filter((v): v is PaymentType => typeof v === 'number')
            .map(v => ({ label: Translate.text(this.props.translations, PaymentType[v]), value: v }));
        return (
            <div className="c-page__content">
                <PageContentHeader text="Tenant" />
                <div className="c-card">
                    <form className="c-form" onSubmit={e => this.submit(e)}>
                        <div className="c-form__group">
                            <label className="c-form__label c-form__label--required" htmlFor="name"><Translator text="name" /></label>
                            <input className="c-form__control" id="name" type="text" required value={this.state.name} onChange={e => this.setState({ name: e.currentTarget.value })} />
                        </div>
                        <div className="c-form__sub-form">
                            <h3><Translator text="Address" /></h3>
                            {this.addressFields('address')}
                        </div>
                        <div className="c-form__sub-form">
                            <h3><Translator text="Payment" /></h3>
                            <div className="c-form__group">
                                <label className="c-form__label" htmlFor="paymentType"><Translator text="Payment type" /></label>
                                <Select
                                    inputId="paymentType"
                                    value={options.find(o => o.value === this.state.paymentType) ?? null}
                                    onChange={o => o && this.setState({ paymentType: o.value })}
                                    options={options}
                                    placeholder={Translate.text(this.props.translations, "Select...")}
                                    className='c-form__select'
                                    classNamePrefix='c-select'
                                />
                            </div>
                            <div className="c-form__group">
                                <label className="c-form__label" htmlFor="invoiceDay"><Translator text="Invoice day" /></label>
                                <input className="c-form__control" id="invoiceDay" type="number" min={1} max={31} required value={this.state.invoiceDay} onChange={e => this.setState({ invoiceDay: e.currentTarget.valueAsNumber })} />
                            </div>
                            <h3><Translator text="Billing address" /></h3>
                            <p><Translator text="Leave empty to use the tenant address" /></p>
                            {this.addressFields('billingAddress')}
                        </div>
                        <div className='c-form__group'>
                            <button className='c-button' type='submit'><Translator text="Save" /></button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        currentTenant: state.currentTenant,
        loginUser: state.loginUser,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchCurrentTenant: bindActionCreators(TenantActions.fetchCurrentTenant, dispatch),
        editCurrentTenant: bindActionCreators(TenantActions.editCurrentTenant, dispatch),
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TenantSettings);
